import { apiClient } from "@/lib/api-client";
import type {
  FluencyPassage,
  FluencyPassageGenerateInput,
  FluencySession,
  FluencyReport,
  FluencySessionListItem,
  FluencySessionStartInput,
  FluencyOutcome,
  StudentSignals,
  KidView,
} from "../types/fluency";

/**
 * Fluency API Client
 * All endpoints are prefixed with /fluency
 */
export const fluencyApi = {
  // ==================== Passages ====================

  async generatePassage(input: FluencyPassageGenerateInput): Promise<FluencyPassage> {
    return apiClient.post<FluencyPassage>("/fluency/passages/generate", input);
  },

  async getStudentSignals(studentId: string): Promise<StudentSignals> {
    return apiClient.get<StudentSignals>("/recommendations/student-signals", {
      params: { student_id: studentId },
    });
  },

  async listPassages(studentId: string): Promise<FluencyPassage[]> {
    return apiClient.get<FluencyPassage[]>("/fluency/passages", {
      params: { student_id: studentId },
    });
  },

  // ==================== Sessions ====================

  async startSession(input: FluencySessionStartInput): Promise<FluencySession> {
    return apiClient.post<FluencySession>("/fluency/sessions", input);
  },

  async markWord(sessionId: string, wordIndex: number): Promise<FluencySession> {
    return apiClient.post<FluencySession>(`/fluency/sessions/${sessionId}/mark`, {
      word_index: wordIndex,
    });
  },

  async unmarkWord(sessionId: string, wordIndex: number): Promise<void> {
    return apiClient.delete<void>(`/fluency/sessions/${sessionId}/mark/${wordIndex}`);
  },

  async endSession(sessionId: string, outcome: FluencyOutcome): Promise<FluencyReport> {
    return apiClient.post<FluencyReport>(`/fluency/sessions/${sessionId}/end`, { outcome });
  },

  async getSession(sessionId: string): Promise<FluencySession> {
    return apiClient.get<FluencySession>(`/fluency/sessions/${sessionId}`);
  },

  async listSessions(studentId: string): Promise<FluencySessionListItem[]> {
    return apiClient.get<FluencySessionListItem[]>("/fluency/sessions", {
      params: { student_id: studentId },
    });
  },

  async startReading(sessionId: string): Promise<void> {
    return apiClient.post<void>(`/fluency/sessions/${sessionId}/start-reading`, {});
  },

  async getKidView(sessionId: string): Promise<KidView> {
    return apiClient.get<KidView>(`/fluency/kid-view/${sessionId}`);
  },
};
